// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';
import { useSubscription } from '@apollo/client';
import { useMap, useMapsLibrary } from '@vis.gl/react-google-maps';
import { useTheme } from '@mui/material';

import { LocationPinMarker } from '@components/Maps';
import { getFilteredPoints } from '@components/DriverTracker/driverTrackerService';
import { GetRunDriverLocationsDocument } from '../queries/getRunDriverLocations.subscription.generated';

/** Strict lat/lng pair — both required. Google Maps' Polyline.path needs this. */
type LatLngLit = { lat: number; lng: number };

// TYPES ---------------------------------------------------------------- //

interface RunDriverMarkerProps {
  /** Mobility run id. Drives the subscription filter. */
  runId: number;
  /** Map id of the parent <Map> so the marker mounts to the correct overlay. */
  mapId: string;
}

/** Hasura returns Postgres `geography` as GeoJSON `{ type, coordinates: [lng, lat] }`. */
interface GeoJsonPoint {
  type?: 'Point';
  coordinates?: [number, number];
}

interface DriverlocationRow {
  id: string;
  location?: GeoJsonPoint | null;
  time: string;
  speed?: number | null;
  heading?: number | null;
}

interface RunDriverLocationsData {
  driverlocations: DriverlocationRow[];
}

interface RunDriverLocationsVars {
  runId: number;
}

// QUERY ---------------------------------------------------------------- //

// The document lives in queries/getRunDriverLocations.subscription.gql. The
// local RunDriverLocationsData types stay because the generated types map the
// `geography` scalar to `object`, which loses the GeoJSON coordinates shape
// toPoint() reads. order_by ASC so the last element in the array is the most
// recent ping (head marker) and the polyline draws in chronological order.

// HELPERS ---------------------------------------------------------------- //

/** Pull [lng, lat] out of a row; returns null for malformed rows. */
const toPoint = (row: DriverlocationRow): LatLngLit | null => {
  const coords = row.location?.coordinates;
  if (!coords || coords.length < 2) return null;
  const [lng, lat] = coords;
  if (!isFinite(lat) || !isFinite(lng)) return null;
  return { lat, lng };
};

/** Narrow a (possibly-partial) Point[] to fully-populated LatLngLit[]. */
const onlyComplete = (
  pts: ({ lat?: number; lng?: number } | undefined)[],
): LatLngLit[] =>
  pts.filter(
    (p): p is LatLngLit =>
      p !== undefined && typeof p.lat === 'number' && typeof p.lng === 'number',
  );

// COMPONENT ---------------------------------------------------------------- //

/**
 * Live driver position + breadcrumb trail for a mobility run.
 *
 * Subscribes to all `driverlocations` rows for this run (cap 5000) and
 * renders two layered overlays inside the parent <RunMapView>:
 *
 *   1. A Polyline through every filtered ping — the "where they've been"
 *      trail. Matches the valet DriverTracker visual: primary color,
 *      strokeWeight 8, 50% opacity. Updates live as new pings arrive.
 *
 *   2. A LocationPinMarker at the most recent point — the "where they are
 *      right now" head marker (z=25, above stop flag markers at z=20).
 *      Rotates by heading when available.
 *
 * No Google Roads API smoothing in v1 (deferred — costs $ per call). Raw
 * filtered breadcrumbs are usable as-is; smoothing can layer in later
 * by mirroring `getSmoothPoints` from the valet flow.
 *
 * Designed to be a child of `RunMapView` — shares the parent map's `mapId`
 * so overlays mount into the correct map instance.
 */
const RunDriverMarker: React.FC<RunDriverMarkerProps> = ({ runId, mapId }) => {
  const theme = useTheme();
  const map = useMap(mapId);
  const mapsLib = useMapsLibrary('maps');

  const { data } = useSubscription<RunDriverLocationsData, RunDriverLocationsVars>(
    GetRunDriverLocationsDocument,
    {
      variables: { runId },
      skip: !runId,
    },
  );

  const rows = React.useMemo(() => data?.driverlocations ?? [], [data]);

  // Build filtered point list for the polyline. Filter is async (valet's util
  // uses an angle-of-three-points algorithm to drop noisy ones). Narrow to
  // fully-populated LatLngLit pairs after filtering so Google Maps' Polyline
  // (which requires non-optional lat/lng) accepts the path directly.
  const [trailPoints, setTrailPoints] = React.useState<LatLngLit[]>([]);
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      if (rows.length === 0) {
        setTrailPoints([]);
        return;
      }
      const raw = rows.map(toPoint).filter((p): p is LatLngLit => p !== null);
      const filtered = onlyComplete(await getFilteredPoints(raw));
      if (!cancelled) setTrailPoints(filtered);
    })();
    return () => {
      cancelled = true;
    };
  }, [rows]);

  // Render the polyline as a Google Maps overlay. Manage lifecycle in an
  // effect because Polyline isn't a React component — it's an imperative
  // Google Maps construct that needs setMap(null) on cleanup.
  const polylineRef = React.useRef<google.maps.Polyline | null>(null);
  React.useEffect(() => {
    if (!map || !mapsLib || trailPoints.length < 2) {
      // Tear down any previous polyline when conditions stop being met.
      if (polylineRef.current) {
        polylineRef.current.setMap(null);
        polylineRef.current = null;
      }
      return;
    }

    // Either create the polyline (first paint) or update its path (subsequent).
    if (!polylineRef.current) {
      polylineRef.current = new mapsLib.Polyline({
        path: trailPoints,
        geodesic: true,
        strokeColor: theme.palette.primary.main,
        strokeOpacity: 0.5,
        strokeWeight: 8,
        zIndex: 8, // analytics-mode "breadcrumb dots" slot per the design system
      });
      polylineRef.current.setMap(map);
    } else {
      polylineRef.current.setPath(trailPoints);
    }

    return () => {
      // Cleanup on unmount only — incremental updates use setPath above.
    };
  }, [map, mapsLib, trailPoints, theme.palette.primary.main]);

  // Final unmount cleanup — separate effect so the dependency-change cleanup
  // above doesn't tear down on every re-render.
  React.useEffect(() => {
    return () => {
      if (polylineRef.current) {
        polylineRef.current.setMap(null);
        polylineRef.current = null;
      }
    };
  }, []);

  // Head marker — most recent ping (ASC order means last element).
  const head = rows[rows.length - 1];
  if (!head) return null;
  const headPoint = toPoint(head);
  if (!headPoint) return null;

  // Heading is degrees clockwise from north when present (per BackgroundGeolocation).
  // Null/negative when the device hasn't been moving long enough to compute one.
  const heading =
    typeof head.heading === 'number' && head.heading >= 0 ? head.heading : undefined;

  return (
    <LocationPinMarker
      mapId={mapId}
      markerId='driver'
      position={headPoint}
      size={48}
      iconSize={28}
      zIndex={25}
      rotation={heading}
      hoverBrighter
    />
  );
};

// EXPORT ---------------------------------------------------------------- //

export default RunDriverMarker;
